import { useContext, useMemo, useState } from "react";
import { haveValue, toast, getUserDisplayName } from "../../helper/common";
import useTranslator from "../../hooks/useTranslator";
import SkorProtectBadge from "../SkorProtectBadge/SkorProtectBadge";
import { Tooltip as ReactTooltip } from "react-tooltip";
import {
  updateTenantShareRequest,
  getTenantShareRequests,
} from "../../services/sharedService";
import LandLordContext from "../../context/landlord/LandLordContext";
import { sortParticipantsWithGuarantors } from "../../helper/applicationHelpers";
import {
  RowButton,
  Avatar,
  Badge,
  Button,
  Checkbox,
  Icon,
  Modal,
  Select,
  SkeletonList,
} from "../design";
import "./pendingApplicationsModal.css";

const getInitials = (name = "") => {
  const parts = String(name || "")
    .trim()
    .split(" ")
    .filter(Boolean);

  if (!parts.length) return "--";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ""}${parts[1][0] || ""}`.toUpperCase();
};

const formatIncome = (value, isAnnual = false) => {
  if (!haveValue(value) || Number.isNaN(Number(value))) return "-";
  const monthlyValue = isAnnual ? Number(value) / 12 : Number(value);
  if (monthlyValue === 0) {
    return null;
  }
  return `${monthlyValue.toLocaleString("es-ES")}€/mo`;
};

const normalizeApplications = (raw = [], _t) => {
  if (!Array.isArray(raw)) return [];

  return raw.map((request, index) => {
    const tenant = request?.tenant_id;
    const application = request?.application_id;
    const isApplication = !!application;

    const tenantFullName =
      `${tenant?.fname || ""} ${tenant?.lname || ""}`.trim();
    const tenantInitials = getInitials(tenantFullName);

    const candidates = [];

    if (!isApplication) {
      candidates.push({
        id: tenant?._id || `tenant-${index}`,
        fullName: tenantFullName || "Inquilino",
        initials: tenantInitials,
        income: tenant?.current_annual_income,
        isAnnualIncome: true,
        email: tenant?.email,
        skorProtect: tenant?.skor_protect || undefined,
      });
    } else {
      // guarantors of the application go right after the tenant they back
      const participants = sortParticipantsWithGuarantors(
        application?.participants || []
      );
      participants.forEach((participant, participantIndex) => {
        const tenantInfo = participant?.tenant_info;
        const participantName = tenantInfo
          ? getUserDisplayName(tenantInfo)
          : participant?.name || `Participant ${participantIndex + 1}`;
        const isMainTenant = participant?.id === tenant?._id;

        candidates.push({
          id: participant?.id || `participant-${index}-${participantIndex}`,
          fullName: participantName,
          initials: getInitials(participantName),
          income: tenantInfo
            ? tenantInfo?.current_annual_income
            : isMainTenant
              ? tenant?.current_annual_income
              : 0,
          isAnnualIncome: false,
          isGuarantor: participant?.is_guarantor === true,
          email: participant?.email,
          consentStatus:
            participant?.consent_status || participant?.consentStatus || "-",
          skorProtect: application?.skor_protect || undefined,
        });
      });
    }

    return {
      id: request?._id || request?.id || index,
      checked: false,
      type: isApplication ? "application" : "profile",
      title: isApplication
        ? application?.title || "Application"
        : tenantFullName || "Inquilino",
      candidates,
      applicationId: application?._id,
      skorProtect:
        application?.skor_protect || tenant?.skor_protect || undefined,
    };
  });
};

const PendingApplicationsModal = ({
  isOpen,
  onClose,
  loading,
  applications,
  workspaces,
  onRefresh,
}) => {
  const { _t } = useTranslator();
  const [selectedWorkspace, setSelectedWorkspace] = useState("");
  const [checkedApplications, setCheckedApplications] = useState(new Set());
  const [isProcessing, setIsProcessing] = useState(false);
  const [expandedApplications, setExpandedApplications] = useState(new Set());
  const { fetchAllLandlordNotification } = useContext(LandLordContext);

  const normalizedApplications = useMemo(
    () => normalizeApplications(applications, _t),
    [applications, _t]
  );

  const toggleExpand = (appId) => {
    setExpandedApplications((prev) => {
      const next = new Set(prev);
      if (next.has(appId)) {
        next.delete(appId);
      } else {
        next.add(appId);
      }
      return next;
    });
  };

  const handleCheckboxChange = (appId) => {
    setCheckedApplications((prev) => {
      const next = new Set(prev);
      if (next.has(appId)) {
        next.delete(appId);
      } else {
        next.add(appId);
      }
      return next;
    });
  };

  const handleSkorProtect = () => {
    window.open(
      "https://www.skortorent.com/inquilino/skor-protect",
      "_blank",
      "noopener,noreferrer"
    );
  };

  const handleUpdateRequests = async (status) => {
    if (!selectedWorkspace || checkedApplications.size === 0) return;

    setIsProcessing(true);

    try {
      const promises = Array.from(checkedApplications).map((requestId) =>
        updateTenantShareRequest(requestId, {
          status,
          workspaceId: selectedWorkspace,
        })
      );

      const responses = await Promise.allSettled(promises);

      const guarantorFailures = responses.filter(
        (response) =>
          response.status === "rejected" &&
          response.reason?.response?.data?.key === "guarantor-not-allowed"
      );
      const allSuccessfulOrGuarantorRemoved = responses.every(
        (response) =>
          response.status === "fulfilled" ||
          response.reason?.response?.data?.key === "guarantor-not-allowed"
      );

      if (allSuccessfulOrGuarantorRemoved) {
        await getTenantShareRequests();
        setCheckedApplications(new Set());

        if (guarantorFailures.length > 0) {
          const guarantorMessage = _t(
            "guarantorUsersAreNotAllowedToShareTheirProfile"
          );
          toast.error(guarantorMessage);
        } else {
          const successMessage =
            status === "accepted"
              ? _t("shareRequestsAccepted") ||
                "Solicitudes aceptadas correctamente"
              : _t("shareRequestsRejected") ||
                "Solicitudes rechazadas correctamente";

          toast.success(successMessage);
        }

        await fetchAllLandlordNotification();
        if (onRefresh) {
          onRefresh();
        }
        onClose();
      } else {
        const errorMessage =
          _t("someRequestsFailed") ||
          "Algunas solicitudes fallaron. Por favor, inténtalo de nuevo.";
        toast.error(errorMessage);
        console.error("Some requests failed");
      }
    } catch (error) {
      const errorMessage =
        _t("requestsUpdateError") ||
        "Error al actualizar las solicitudes. Por favor, inténtalo de nuevo.";
      toast.error(errorMessage);
      console.error("Error updating requests:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  const isChecked = (id) => checkedApplications.has(id);

  const renderSkorProtect = (application) =>
    application.skorProtect && (
      <>
        <span data-tooltip-id={`isf-sp-badge-tooltip-${application.id}`}>
          <SkorProtectBadge />
        </span>
        <ReactTooltip
          id={`isf-sp-badge-tooltip-${application.id}`}
          place="bottom"
          className="react-tooltip-styling react-tooltip-styling-skor-protect"
          clickable
          style={{ maxWidth: "20.625rem", whiteSpace: "normal" }}
          render={() => (
            <p className="pendingTenants__tip">
              {_t("skorProtectTooltipDescription")}{" "}
              <span
                className="pendingTenants__tipLink"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleSkorProtect();
                }}
              >
                {_t("skorProtectTooltipLink")}
              </span>
            </p>
          )}
        />
      </>
    );

  const renderProfile = (application) => {
    const candidate = application.candidates[0];
    return (
      <div key={application.id} className="pendingTenants__card">
        <div className="pendingTenants__header">
          <Checkbox
            checked={isChecked(application.id)}
            onChange={() => handleCheckboxChange(application.id)}
            ariaLabel={candidate.fullName}
          />
          <Avatar name={candidate.fullName} size="md" />
          <div className="pendingTenants__main">
            <p className="pendingTenants__name">{candidate.fullName}</p>
            {candidate.consentStatus === "pending" && (
              <Badge tone="warning">{_t("pending") || "Pendiente"}</Badge>
            )}
            {renderSkorProtect(application)}
          </div>
        </div>
      </div>
    );
  };

  const renderApplication = (application) => {
    const isExpanded = expandedApplications.has(application.id);
    return (
      <div key={application.id} className="pendingTenants__card">
        <div
          className="pendingTenants__header is-clickable"
          onClick={() => toggleExpand(application.id)}
        >
          <span onClick={(e) => e.stopPropagation()}>
            <Checkbox
              checked={isChecked(application.id)}
              onChange={() => handleCheckboxChange(application.id)}
              ariaLabel={application.title}
            />
          </span>
          <span className="pendingTenants__avatarWrap">
            <Avatar name={application.title} size="md" />
            <Badge tone="ink" className="pendingTenants__count">
              {application.candidates.length}
            </Badge>
          </span>
          <div className="pendingTenants__main">
            <RowButton
              type="button"
              className="pendingTenants__toggle"
              aria-expanded={isExpanded}
            >
              <span className="pendingTenants__name">{application.title}</span>
              <Icon
                name="chevronRight"
                size={16}
                className={`pendingTenants__chevron ${isExpanded ? "is-open" : ""}`}
              />
            </RowButton>
            <span onClick={(e) => e.stopPropagation()}>
              {renderSkorProtect(application)}
            </span>
          </div>
        </div>

        {isExpanded && (
          <div className="pendingTenants__candidates">
            {application.candidates.map((candidate) => (
              <div key={candidate.id}>
                <div className="pendingTenants__candidate">
                  <Avatar name={candidate.fullName} size="sm" />
                  <div className="pendingTenants__candidateInfo">
                    <div className="pendingTenants__candidateTitle">
                      <p
                        className="pendingTenants__candidateName"
                        title={candidate.fullName}
                      >
                        {candidate.fullName}
                      </p>
                      {candidate.isGuarantor && (
                        <Badge tone="neutral" size="xs">
                          {_t("guarantor", "Guarantor")}
                        </Badge>
                      )}
                    </div>
                    {candidate.consentStatus === "pending" ? (
                      <Badge
                        tone="warning"
                        className="pendingTenants__candidateBadge"
                      >
                        {_t("pending") || "Pendiente"}
                      </Badge>
                    ) : (
                      <p className="pendingTenants__income">
                        {_t("income")}:{" "}
                        {formatIncome(
                          candidate.income,
                          candidate.isAnnualIncome
                        )}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      size="md"
      align="start"
      title={`${_t("pendingApplications") || "Solicitudes pendientes"} (${
        normalizedApplications?.length || 0
      })`}
      closeLabel={_t("close")}
      actionsLayout="row"
      actions={
        <>
          <Button
            variant="secondary"
            size="lg"
            disabled={isProcessing || checkedApplications.size === 0}
            onClick={() => handleUpdateRequests("rejected")}
          >
            {_t("reject") || "RECHAZAR"}
          </Button>
          <Button
            variant="primary"
            size="lg"
            loading={isProcessing}
            disabled={
              isProcessing ||
              !selectedWorkspace ||
              checkedApplications.size === 0
            }
            onClick={() => handleUpdateRequests("accepted")}
          >
            {_t("accept") || "ACEPTAR"}
          </Button>
        </>
      }
    >
      {loading ? (
        <SkeletonList rows={3} variant="card" label={_t("loading")} />
      ) : normalizedApplications.length === 0 ? (
        <p className="pendingTenants__empty">
          {_t("noPendingApplications") || "No hay solicitudes pendientes."}
        </p>
      ) : (
        <div className="pendingTenants__list">
          {normalizedApplications.map((application) =>
            application.type === "profile"
              ? renderProfile(application)
              : renderApplication(application)
          )}
        </div>
      )}

      <div className="pendingTenants__workspace">
        <Select
          id="pending-tenants-workspace"
          label={
            _t("selectWorkspaceToSaveRequest") ||
            "¿En qué workspace quieres guardar esta solicitud?"
          }
          placeholder={_t("selectWorkspace") || "Selecciona un workspace..."}
          value={selectedWorkspace}
          onChange={(e) => setSelectedWorkspace(e.target.value)}
          options={(workspaces || []).map((workspace) => ({
            value: workspace?._id,
            label: workspace?.name,
          }))}
        />
      </div>
    </Modal>
  );
};

export default PendingApplicationsModal;
